import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import EntityLogo from "@/components/EntityLogo";
import PlayerMetricsSection from "@/components/PlayerMetricsSection";
import EvaluationCard from "@/components/EvaluationCard";
import ShareButton from "@/components/ShareButton";
import CopyLinkButton from "@/components/CopyLinkButton";
import { track } from "@/lib/analytics";
import { FeaturedVideo, MediaThumb, VideoModal, CareerTimeline } from "@/components/PlayerMediaPublic";
import { DevelopmentSummary, DevelopmentCharts } from "@/components/PlayerDevelopment";
import RecruitingMomentum from "@/components/RecruitingMomentum";
import SchoolLogo from "@/components/SchoolLogo";
import PlayerStatisticsTab from "@/components/player/PlayerStatisticsTab";
import PlayerGameLogTab from "@/components/player/PlayerGameLogTab";
import RecentPerformance from "@/components/player/RecentPerformance";
import PlayerInGameScore from "@/components/player/PlayerInGameScore";
import { Download, Award, LayoutGrid, ArrowRight, Instagram, Twitter, Youtube, Music2, Globe, Video, Link2, ExternalLink, Mail, FileText, Lock } from "lucide-react";

const LINK_TARGET = {
  instagram_handle: "instagram", x_handle: "x", tiktok_handle: "tiktok", youtube_handle: "youtube",
  perfect_game_url: "perfect_game", prep_baseball_url: "prep_baseball", ncsa_url: "ncsa",
  fieldlevel_url: "fieldlevel", img_url: "img", highlight_video_url: "highlight_video",
  hudl_url: "hudl", personal_website_url: "personal_website",
};

const TABS = [
  ["overview", "Overview"], ["metrics", "Metrics"], ["evaluations", "Evaluations"],
  ["statistics", "Statistics"], ["gamelog", "Game Log"], ["media", "Media"], ["timeline", "Timeline"],
];

export default function PlayerProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [player, setPlayer] = useState(null);
  const [evals, setEvals] = useState([]);
  const [evalAccess, setEvalAccess] = useState(null);
  const [ranks, setRanks] = useState(null);
  const [cardOptions, setCardOptions] = useState([]);
  const [awards, setAwards] = useState(null);
  const [articles, setArticles] = useState([]);
  const [media, setMedia] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [statFilters, setStatFilters] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const tablistRef = useRef(null);

  const rawTab = params.get("tab");
  const tab = TABS.some(([k]) => k === rawTab) ? rawTab : "overview";
  const setTab = (t) => { const p = new URLSearchParams(params); p.set("tab", t); setParams(p, { replace: true }); };

  const openVideo = (m, source) => {
    if (m && player) track("video_view", player.id, source, { media_id: m.id, video_type: m.video_type });
    setVideoPreview(m);
  };

  useEffect(() => {
    api.get(`/players/${slug}`)
      .then(({ data }) => {
        setPlayer(data);
        if (data.slug && data.slug !== slug) navigate(`/prospects/${data.slug}`, { replace: true });
        document.title = `${data.first_name} ${data.last_name} | ${data.graduation_year || "CBR Prospect"} ${data.primary_position || ""} | CBR Prospects`;
        api.get(`/players/${data.id}/rankings`).then((r) => setRanks(r?.data || null)).catch(() => setRanks(null));
        api.get(`/players/${data.id}/card-options`).then((r) => setCardOptions(r?.data?.options || [])).catch(() => setCardOptions([]));
        api.get(`/players/${data.id}/awards`).then((r) => setAwards(r?.data || null)).catch(() => setAwards(null));
        api.get(`/players/${data.id}/articles`).then((r) => setArticles(r?.data?.items || [])).catch(() => setArticles([]));
        api.get(`/players/${data.id}/media`).then((r) => setMedia(r?.data?.items || [])).catch(() => setMedia([]));
        api.get(`/players/${data.id}/timeline`).then((r) => setTimeline(r?.data?.items || [])).catch(() => setTimeline([]));
        api.get(`/players/${data.id}/stat-filters`).then((r) => setStatFilters(r?.data || null)).catch(() => setStatFilters(null));
        return api.get("/evaluations", { params: { player_id: data.id, status_filter: "submitted" } });
      })
      .then((res) => { setEvals(res?.data?.items || []); setEvalAccess(res?.data || null); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    return () => { document.title = "CBR Prospects"; };
  }, [slug, navigate]);

  const onTabKey = (e) => {
    const i = TABS.findIndex(([k]) => k === tab);
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const ni = e.key === "ArrowRight" ? (i + 1) % TABS.length : (i - 1 + TABS.length) % TABS.length;
      setTab(TABS[ni][0]);
      requestAnimationFrame(() => tablistRef.current?.querySelector(`[data-tabkey="${TABS[ni][0]}"]`)?.focus());
    }
  };

  if (loading) return <PublicLayout><div className="max-w-7xl mx-auto p-10 label-overline">Loading prospect…</div></PublicLayout>;
  if (notFound || !player) {
    return (
      <PublicLayout>
        <div className="max-w-7xl mx-auto p-10">
          <div className="font-display text-3xl uppercase">Prospect not found</div>
          <Link to="/prospects" className="text-[#E63946] text-sm underline mt-3 inline-block">← All prospects</Link>
        </div>
      </PublicLayout>
    );
  }

  const hasStats = !!statFilters?.has_approved_data;
  const featured = media.find((m) => m.is_featured);
  const restMedia = media.filter((m) => !m.is_featured);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative bg-[#0A192F] text-white">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1651526863031-070aa9bdcb5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxiYXNlYmFsbCUyMGdhbWUlMjBuaWdodCUyMGxpZ2h0c3xlbnwwfHx8fDE3ODE3MTU5MDR8MA&ixlib=rb-4.1.0&q=85')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/85 to-[#0A192F]/60" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-3 flex justify-center lg:justify-start">
            <div className="w-40 h-40 bg-slate-700 border-4 border-white grid place-items-center overflow-hidden rounded-sm">
              {player.profile_photo_url ? (
                <img src={player.profile_photo_url} alt={`${player.first_name} ${player.last_name} headshot`} className="w-full h-full object-cover" />
              ) : (
                <span className="font-display text-5xl text-white">{player.first_name?.[0]}{player.last_name?.[0]}</span>
              )}
            </div>
          </div>
          <div className="lg:col-span-9">
            <div className="flex flex-wrap gap-2">
              {player.recruiting?.commitment_status && player.recruiting.commitment_status !== "uncommitted" && !player.commitment?.school_name && (
                <span className="bg-[#E63946] text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-1">{player.recruiting.commitment_status}</span>
              )}
              <span className="border border-white/30 text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-1">Class of {player.graduation_year || "—"}</span>
              {awards && awards.total > 0 && (
                <span data-testid="player-awards-badge" className="inline-flex items-center gap-1 bg-[#E63946] text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-1"><Award className="w-3 h-3" /> {awards.total} CBR Award{awards.total > 1 ? "s" : ""}</span>
              )}
              {player.commitment && player.commitment.school_name && (
                <span data-testid="player-commitment-badge" className="inline-flex items-center gap-1.5 bg-white text-[#0A192F] text-[10px] font-semibold uppercase tracking-wider px-2 py-1">
                  {player.commitment.school_logo_url ? <SchoolLogo name={player.commitment.school_name} logoUrl={player.commitment.school_logo_url} size="sm" className="!w-4 !h-4 !border-0" /> : <Award className="w-3 h-3 text-[#E63946]" />}
                  {player.commitment.level || "Committed"} · {player.commitment.school_name}
                </span>
              )}
              {player.complete_recruiting_profile && (
                <span data-testid="complete-recruiting-badge" className="inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-1"><Award className="w-3 h-3" /> Complete Recruiting Profile</span>
              )}
            </div>
            <h1 data-testid="player-name" className="font-display text-5xl lg:text-7xl uppercase tracking-tighter mt-4 leading-[0.95]">{player.first_name}{" "}<span className="text-[#E63946]">{player.last_name}</span></h1>
            <div className="mt-4 flex flex-wrap gap-6 text-sm text-white/80">
              <div><span className="label-overline text-white/50">Position</span><div data-testid="player-positions" className="font-data text-lg">{player.primary_position || "—"}{player.secondary_position ? <span className="text-white/50"> / {player.secondary_position}</span> : ""}</div></div>
              <div><span className="label-overline text-white/50">B/T</span><div className="font-data text-lg">{player.bats || "—"}/{player.throws || "—"}</div></div>
              <div><span className="label-overline text-white/50">Height</span><div className="font-data text-lg">{player.height_cm ? `${player.height_cm} cm` : "—"}</div></div>
              <div><span className="label-overline text-white/50">Weight</span><div className="font-data text-lg">{player.weight_kg ? `${player.weight_kg} kg` : "—"}</div></div>
              <div><span className="label-overline text-white/50">Hometown</span><div className="font-data text-lg">{player.city || "—"}, {player.province || ""}</div></div>
              {player.age != null && <div data-testid="player-age"><span className="label-overline text-white/50">Age</span><div className="font-data text-lg">{player.age}</div></div>}
            </div>
            <div className="mt-6">
              <ShareButton url={`${window.location.origin}/prospects/${player.slug}`} title={`${player.first_name} ${player.last_name} · ${player.graduation_year || ""} ${player.primary_position || ""} · CBR Prospects`} />
            </div>
          </div>
        </div>
      </section>

      {/* Sticky tab bar */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div ref={tablistRef} role="tablist" aria-label="Player profile sections" onKeyDown={onTabKey} className="flex gap-1 overflow-x-auto no-scrollbar">
            {TABS.map(([k, label]) => {
              const active = tab === k;
              return (
                <button key={k} role="tab" data-tabkey={k} data-testid={`tab-${k}`} id={`tab-${k}`}
                  aria-selected={active} aria-controls={`panel-${k}`} tabIndex={active ? 0 : -1} onClick={() => setTab(k)}
                  className={`px-4 py-3.5 text-sm font-semibold uppercase tracking-wider whitespace-nowrap border-b-2 -mb-px transition-colors focus-visible:ring-2 focus-visible:ring-[#E63946] focus-visible:outline-none ${active ? "border-[#E63946] text-[#0A192F]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Body */}
      <section className="bg-white py-10 lg:py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 min-w-0">
            <div key={tab} role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`} className="animate-fadein space-y-8">
              {tab === "overview" && (
                <>
                  <DevelopmentSummary playerId={player.id} />
                  <RecruitingMomentum playerId={player.id} />
                  <RecentPerformance playerId={player.id} hasData={hasStats} onGoTo={setTab} />
                  <div>
                    <div className="label-overline text-[#E63946]">About</div>
                    <h2 className="font-display text-2xl uppercase tracking-tight mt-2">Player Bio</h2>
                    <p className="mt-3 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{player.bio || "No bio provided yet."}</p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="border border-slate-200 p-5"><div className="label-overline">School</div><div className="font-semibold mt-1">{player.school || "—"}</div></div>
                    <div className="border border-slate-200 p-5">
                      <div className="label-overline">Current Team</div>
                      {player.current_team_info ? (
                        <a href={player.current_team_info.slug ? `/live/teams/${player.current_team_info.slug}` : "#"} data-testid="player-current-team" className="mt-1 flex items-center gap-2 group">
                          <EntityLogo src={player.current_team_info.effective_logo_url} alt={player.current_team_info.name} size="w-8 h-8" textSize="text-[8px]" />
                          <span className="min-w-0"><span className="font-semibold block truncate group-hover:text-[#E63946]">{player.current_team_info.name}</span>{player.current_team_info.organization_name && <span className="text-xs text-slate-500 block truncate">{player.current_team_info.organization_name}</span>}</span>
                        </a>
                      ) : (
                        <div className="font-semibold mt-1" data-testid="player-current-team">{player.current_team || "—"}</div>
                      )}
                    </div>
                    <div className="border border-slate-200 p-5"><div className="label-overline">GPA</div><div className="font-data text-2xl mt-1">{player.gpa || "—"}</div></div>
                    <div className="border border-slate-200 p-5"><div className="label-overline">Commitment</div><div className="font-semibold mt-1">{player.recruiting?.committed_to || "Uncommitted"}</div></div>
                  </div>
                  <Link to="/media-wall" data-testid="player-media-wall-link" className="flex items-center justify-between gap-3 bg-[#0A192F] text-white rounded-sm p-4 hover:bg-[#1a2a4a] transition-colors">
                    <div className="flex items-center gap-3"><LayoutGrid className="w-5 h-5 text-[#E63946]" /><span className="font-display uppercase tracking-tight text-sm">See CBR verified graphics on the Media Wall</span></div>
                    <ArrowRight className="w-4 h-4 shrink-0" />
                  </Link>
                </>
              )}

              {tab === "metrics" && (
                <>
                  <PlayerMetricsSection player={player} />
                  <DevelopmentCharts playerId={player.id} />
                </>
              )}

              {tab === "evaluations" && (
                evalAccess?.can_view ? (
                  evals.length > 0 ? (
                    <div>
                      <div className="label-overline text-[#E63946]">Scouting</div>
                      <h3 className="font-display text-2xl uppercase tracking-tight mt-1">CBR Evaluations</h3>
                      <div className="mt-4 space-y-3">{evals.map((e) => <EvaluationCard key={e.id} evaluation={e} hideEvaluator={evalAccess?.access_level !== "admin"} />)}</div>
                    </div>
                  ) : (
                    <div data-testid="evaluations-empty" className="border border-dashed border-slate-300 rounded-sm p-6 text-center text-sm text-slate-500">No submitted CBR evaluations yet.</div>
                  )
                ) : (
                  <LockedReport access={evalAccess} />
                )
              )}

              {tab === "statistics" && <PlayerStatisticsTab playerId={player.id} filters={statFilters} />}

              {tab === "gamelog" && <PlayerGameLogTab playerId={player.id} filters={statFilters} />}

              {tab === "media" && (
                <>
                  {media.length > 0 ? (
                    <div data-testid="media-library-section">
                      <div className="label-overline text-[#E63946]">Film Room</div>
                      <h3 className="font-display text-2xl uppercase tracking-tight mt-1 mb-4">Media Library</h3>
                      <FeaturedVideo media={featured} onOpen={(m) => openVideo(m, "featured")} />
                      {restMedia.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{restMedia.map((m) => <MediaThumb key={m.id} media={m} testid={`media-item-${m.id}`} onClick={() => openVideo(m, "media_library")} />)}</div>
                      )}
                    </div>
                  ) : (
                    <div data-testid="media-empty" className="border border-dashed border-slate-300 rounded-sm p-6 text-center text-sm text-slate-500">No media uploaded yet.</div>
                  )}
                  {articles.length > 0 && (
                    <div data-testid="player-articles-section">
                      <div className="label-overline text-[#E63946]">Coverage</div>
                      <h3 className="font-display text-2xl uppercase tracking-tight mt-1">In the News</h3>
                      <div className="mt-4 space-y-2">
                        {articles.map((a) => (
                          <Link key={a.id} to={`/news/${a.slug}`} className="flex items-center gap-3 border border-slate-200 rounded-sm p-3 hover:border-[#0A192F] transition-colors">
                            <div className="w-12 h-12 rounded-sm bg-slate-100 overflow-hidden shrink-0">{a.cover_image_url ? <img src={a.cover_image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-slate-300 text-xs">CBR</div>}</div>
                            <div className="min-w-0"><div className="text-[10px] uppercase tracking-wider text-[#E63946]">{a.category?.replace(/_/g, " ")}</div><div className="font-semibold text-sm truncate">{a.title}</div></div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {tab === "timeline" && (
                <>
                  <CareerTimeline items={timeline} />
                  {awards && awards.total > 0 && (
                    <div data-testid="player-awards-section">
                      <div className="label-overline text-[#E63946]">Recognition</div>
                      <h3 className="font-display text-2xl uppercase tracking-tight mt-1 flex items-center gap-2"><Award className="w-5 h-5 text-[#E63946]" /> Camp Awards</h3>
                      <div className="mt-4 space-y-4">
                        {awards.camps.map((c) => (
                          <div key={c.camp_id} className="border border-slate-200 rounded-sm p-4">
                            <Link to={c.camp_slug ? `/camps/${c.camp_slug}/awards` : "#"} className="font-semibold text-[#0A192F] hover:text-[#E63946]">{c.camp_year ? `${c.camp_year} ` : ""}{c.camp_title}</Link>
                            <ul className="mt-2 space-y-1">
                              {c.awards.map((a) => (
                                <li key={a.award_key} className="flex items-center gap-2 text-sm text-slate-700"><Award className="w-3.5 h-3.5 text-[#E63946] shrink-0" /><span className="font-medium">{a.award_label}</span>{a.stat_value && <span className="text-xs text-slate-400">· {a.stat_value}{a.stat_units ? ` ${a.stat_units}` : ""} {a.stat_label}</span>}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {timeline.length === 0 && (!awards || awards.total === 0) && (
                    <div data-testid="timeline-empty" className="border border-dashed border-slate-300 rounded-sm p-6 text-center text-sm text-slate-500">No career timeline events yet.</div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Sidebar — persistent across all tabs */}
          <aside className="lg:col-span-4 space-y-4">
            <div className="border border-slate-200 p-6 bg-gradient-to-br from-white to-slate-50">
              <div className="label-overline text-[#E63946]">CBR Overall Rating</div>
              <div className="font-data text-6xl mt-2 text-[#0A192F]">{player.cbr_overall_rating != null ? Math.round(player.cbr_overall_rating * 10) / 10 : "—"}</div>
              {player.cbr_overall_rating != null ? (
                <p className="text-xs text-slate-600 mt-2">20-80 scouting scale · blended verified metrics &amp; CBR evaluator OFV.</p>
              ) : (
                <p className="text-xs text-slate-500 mt-2">Activates after first verified metric or submitted evaluation.</p>
              )}
            </div>
            <PlayerInGameScore playerId={player.id} />
            {player.commitment && player.commitment.school_name && (
              <div className="border border-[#0A192F] p-6 bg-[#0A192F] text-white" data-testid="profile-commitment-card">
                <div className="label-overline text-[#E63946]">College Commitment</div>
                <div className="flex items-center gap-3 mt-3">
                  <SchoolLogo name={player.commitment.school_name} logoUrl={player.commitment.school_logo_url} darkLogoUrl={player.commitment.school_logo_dark_url} dark size="lg" testid="commitment-school-logo" />
                  <div className="min-w-0"><div className="font-display text-2xl uppercase tracking-tight leading-tight">{player.commitment.school_name}</div>{player.commitment.sport && <div className="text-xs text-white/60 mt-0.5">{player.commitment.sport}</div>}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-white/70">
                  {player.commitment.level && <span className="bg-white/10 px-2 py-0.5 rounded-sm uppercase tracking-wider">{player.commitment.level}</span>}
                  {player.commitment.scholarship_type && <span className="bg-white/10 px-2 py-0.5 rounded-sm uppercase tracking-wider">{player.commitment.scholarship_type}</span>}
                  <span className="uppercase tracking-wider">{(player.commitment.commitment_status || "").replace(/_/g, " ")}</span>
                  {player.commitment.commitment_date && <span>{new Date(player.commitment.commitment_date).toLocaleDateString("en-CA")}</span>}
                </div>
              </div>
            )}
            <div className="border border-slate-200 p-6"><div className="label-overline">Evaluations</div><div className="font-data text-4xl mt-2">{evals.length}</div><p className="text-xs text-slate-500 mt-2">Submitted by CBR evaluators.</p></div>
            <div className="border border-slate-200 p-6" data-testid="profile-rankings-card">
              <div className="label-overline">CBR Rankings</div>
              {ranks && ranks.public_visible !== false && ranks.national ? (
                <div className="mt-3 space-y-2.5">
                  <RankBadge label="National" rank={ranks.national} total={ranks.national_total} highlight />
                  {ranks.provincial && <RankBadge label={`${ranks.province} Provincial`} rank={ranks.provincial} total={ranks.provincial_total} />}
                  {ranks.position && <RankBadge label={`${ranks.primary_position} Position`} rank={ranks.position} total={ranks.position_total} />}
                  {ranks.graduation_year_rank && <RankBadge label={`Class of ${ranks.graduation_year}`} rank={ranks.graduation_year_rank} total={ranks.graduation_year_total} />}
                </div>
              ) : (
                <><div className="font-data text-4xl mt-2">—</div><p className="text-xs text-slate-500 mt-2">Ranking activates once this prospect has a CBR Overall Rating.</p></>
              )}
            </div>
            <div className="border border-slate-200 p-6" data-testid="share-bar">
              <div className="label-overline">Recruiting Profile Link</div>
              <div className="font-mono text-xs mt-2 break-all bg-slate-50 p-2">{window.location.origin}/prospects/{player.slug}</div>
              <div className="mt-3 space-y-2">
                <CopyLinkButton url={`${window.location.origin}/prospects/${player.slug}`} onCopied={() => track("copy_profile_link", player.id, "public_profile")} />
                <Link to={`/portfolio/${player.slug}`} data-testid="view-portfolio-link" className="flex items-center justify-center gap-2 px-3 py-2 bg-[#E63946] text-white rounded-sm text-xs font-semibold uppercase tracking-wider hover:bg-[#c62f3b] transition-colors"><FileText className="w-4 h-4" /> Recruiting Portfolio</Link>
                <div className="grid grid-cols-2 gap-2">
                  <a data-testid="share-email" href={`mailto:?subject=${encodeURIComponent(`${player.first_name} ${player.last_name} · CBR Prospects`)}&body=${encodeURIComponent(`Check out ${player.first_name} ${player.last_name} on CBR Prospects:\n${window.location.origin}/prospects/${player.slug}`)}`} className="inline-flex items-center justify-center gap-2 px-3 py-2 border border-slate-300 rounded-sm text-xs font-semibold uppercase tracking-wider text-[#0A192F] hover:bg-slate-50 transition-colors"><Mail className="w-4 h-4" /> Email</a>
                  <a data-testid="share-x" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out ${player.first_name} ${player.last_name} on CBR Prospects:`)}&url=${encodeURIComponent(`${window.location.origin}/prospects/${player.slug}`)}&hashtags=CBRProspects,CanadianBaseball`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 px-3 py-2 border border-slate-300 rounded-sm text-xs font-semibold uppercase tracking-wider text-[#0A192F] hover:bg-slate-50 transition-colors">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2H21.5l-7.5 8.57L23 22h-6.937l-5.45-7.13L4.5 22H1.244l8.022-9.166L1 2h7.06l4.93 6.522L18.244 2zm-2.43 18.05h1.92L7.27 3.85H5.207L15.814 20.05z"/></svg>
                    Share on X
                  </a>
                </div>
              </div>
            </div>
            <RecruitingLinks player={player} />
            {cardOptions.length > 0 && (
              <div className="border border-slate-200 p-6" data-testid="shareable-cards">
                <div className="label-overline text-[#E63946]">Shareable Cards</div>
                <p className="text-xs text-slate-500 mt-1 mb-3">Download a branded CBR graphic to share on social.</p>
                <div className="flex flex-col gap-2">
                  {cardOptions.map((o) => (
                    <a key={o.type} data-testid={`card-download-${o.type}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/players/${player.id}/card.png?type=${o.type}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider border border-slate-300 rounded-sm hover:border-[#E63946] hover:text-[#E63946]">{o.label}<Download className="w-3.5 h-3.5" /></a>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>
      <VideoModal media={videoPreview} onClose={() => setVideoPreview(null)} />
    </PublicLayout>
  );
}

function RecruitingLinks({ player }) {
  const handle = (h) => String(h || "").replace(/^@/, "");
  const links = [
    { key: "instagram_handle", label: "Instagram", icon: Instagram, url: player.instagram_handle && `https://instagram.com/${handle(player.instagram_handle)}` },
    { key: "x_handle", label: "X / Twitter", icon: Twitter, url: player.x_handle && `https://x.com/${handle(player.x_handle)}` },
    { key: "tiktok_handle", label: "TikTok", icon: Music2, url: player.tiktok_handle && `https://tiktok.com/@${handle(player.tiktok_handle)}` },
    { key: "youtube_handle", label: "YouTube", icon: Youtube, url: player.youtube_handle && (String(player.youtube_handle).startsWith("http") ? player.youtube_handle : `https://youtube.com/@${handle(player.youtube_handle)}`) },
    { key: "perfect_game_url", label: "Perfect Game", icon: ExternalLink, url: player.perfect_game_url },
    { key: "prep_baseball_url", label: "Prep Baseball Report", icon: ExternalLink, url: player.prep_baseball_url },
    { key: "ncsa_url", label: "NCSA", icon: ExternalLink, url: player.ncsa_url },
    { key: "fieldlevel_url", label: "FieldLevel", icon: ExternalLink, url: player.fieldlevel_url },
    { key: "img_url", label: "IMG Academy", icon: ExternalLink, url: player.img_url },
    { key: "highlight_video_url", label: "Highlight Video", icon: Video, url: player.highlight_video_url },
    { key: "hudl_url", label: "Hudl", icon: Video, url: player.hudl_url },
    { key: "personal_website_url", label: "Personal Website", icon: Globe, url: player.personal_website_url },
  ].filter((l) => l.url);
  if (links.length === 0) return null;
  return (
    <div className="border border-slate-200 p-6" data-testid="recruiting-links-card">
      <div className="flex items-center gap-2 label-overline text-[#E63946]"><Link2 className="w-4 h-4" /> Connect & Recruiting Profiles</div>
      <div className="mt-4 grid grid-cols-1 gap-2">
        {links.map((l) => (
          <a key={l.key} href={l.url} target="_blank" rel="noreferrer" data-testid={`recruiting-link-${l.key}`} onClick={() => track("link_click", player.id, "public_profile", { target: LINK_TARGET[l.key] || l.key, url: l.url })} className="flex items-center gap-3 px-3 py-2.5 border border-slate-200 rounded-sm hover:border-[#0A192F] hover:bg-slate-50 transition-colors group">
            <l.icon className="w-4 h-4 text-[#0A192F] shrink-0" /><span className="text-sm font-semibold text-[#0A192F] flex-1 truncate">{l.label}</span><ExternalLink className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#E63946] shrink-0" />
          </a>
        ))}
      </div>
    </div>
  );
}

function LockedReport({ access }) {
  if (!access) return <div className="p-6 text-sm text-slate-400">Loading…</div>;
  const cta = access.cta || {};
  const score = access.overall_public_score;
  return (
    <div data-testid="evaluation-locked-panel">
      <div className="label-overline text-[#E63946]">Scouting</div>
      <h3 className="font-display text-2xl uppercase tracking-tight mt-1">{cta.title || "Detailed 20/80 Scouting Report"}</h3>
      <div className="mt-4 border border-slate-200 rounded-sm overflow-hidden">
        {score != null && (
          <div className="bg-[#0A192F] text-white px-5 py-4 flex items-center justify-between">
            <div>
              <div className="label-overline text-white/50 text-[10px]">Overall CBR Score</div>
              <div className="text-xs text-white/60 mt-0.5">Publicly visible</div>
            </div>
            <div className="font-data text-4xl text-[#E63946]" data-testid="evaluation-public-score">{score}</div>
          </div>
        )}
        <div className="p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-slate-100 grid place-items-center mx-auto">
            <Lock className="w-5 h-5 text-slate-400" />
          </div>
          <p className="mt-4 text-sm text-slate-600 max-w-md mx-auto leading-relaxed" data-testid="evaluation-locked-message">{cta.message}</p>
          {access.has_detailed_evaluation === false && (
            <p className="mt-2 text-xs text-slate-400">A detailed report will appear here once an evaluation is on file.</p>
          )}
          {cta.button && (
            <Link to={cta.button_href || "/membership"} data-testid="evaluation-membership-cta"
              className="inline-flex items-center gap-2 mt-5 bg-[#E63946] text-white px-5 py-2.5 rounded-sm text-xs font-semibold uppercase tracking-wider hover:bg-[#c92b38] transition-colors">
              {cta.button} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function RankBadge({ label, rank, total, highlight }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-600">{label}</span>
      <span className={`font-data ${highlight ? "text-2xl text-[#E63946]" : "text-lg text-[#0A192F]"}`}>#{rank}{total ? <span className="text-[11px] text-slate-400 ml-1">/ {total}</span> : null}</span>
    </div>
  );
}
